"use client";

import { buttonVariants } from "@/components/ui/button";
import ArrowExternal from "@/components/ui/icons/arrow-external";
import { Label } from "@/components/ui/label";
import {
  HybridTooltip,
  HybridTooltipTrigger,
} from "@/components/ui/tooltip/hybrid-tooltip";
import { HybridTooltipContent } from "@/components/ui/tooltip/hybrid-tooltip";
import { cn } from "@/lib/utils";
import { reportClick } from "@/shared/lib/analytics";
import { getActiveTier } from "@/shared/models";
import { useUser } from "@/shared/providers/user-provider";
import { Settings } from "lucide-react";
import Link from "next/link";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { type ReactNode, useEffect, useState } from "react";
import { Lightning } from "../icons/Lightning";

export interface NavItem {
  title: string;
  href?: string;
  label?: string;
  icon?: ReactNode;
  selected?: boolean;
  group?: boolean;
  submenu?: NavItem[];
  isUpgrade?: boolean;
  external?: boolean;
}

interface NavProps {
  items: NavItem[];
  compact?: boolean;
  onNav?: (item: NavItem) => void;
  variant?: "vertical" | "horizontal" | "tab";
  className?: string;
}

const NavLink = ({
  item,
  compact,
  sub,
  onNav,
  isModifierKeyPressed,
  variant = "vertical",
  shouldShowUpgrade,
}: {
  item: NavItem;
  compact: boolean;
  sub?: boolean;
  onNav?: (item: NavItem) => void;
  isModifierKeyPressed: boolean;
  variant?: "vertical" | "horizontal" | "tab";
  shouldShowUpgrade: boolean;
}) => {
  const flag = useFeatureFlagEnabled("projects");
  const { stytchUser } = useUser();

  return (
    <Link
      href={item.href ? item.href : ""}
      prefetch={true}
      onClick={() => {
        if (!isModifierKeyPressed && onNav) {
          onNav(item);
        }
        reportClick(`nav_click_${item.title.toLowerCase().replace(/ /g, "_")}`);
      }}
      className={cn(
        buttonVariants({ variant: "nav" }),
        "justify-start pl-3 text-muted-foreground",
        item.selected && "text-primaryaccent font-medium",
        variant === "tab" &&
          item.selected &&
          "underline underline-offset-8 text-primaryaccent",
        sub && "pl-3 ml-6",
        variant === "horizontal" || variant === "tab" ? "px-0 pr-6" : "p-0",
        compact && "h-7",
        !shouldShowUpgrade && item.isUpgrade && "invisible",
      )}
    >
      {flag && stytchUser && (
        <div
          className={cn(
            "lg:hidden w-8 h-4 mr-1 flex items-center justify-center",
            item.selected && "dark:text-primaryaccent",
          )}
        >
          <span
            className={cn(
              "dark:text-foreground",
              item.selected && "dark:text-primaryaccent",
            )}
          >
            {item.icon}
          </span>
        </div>
      )}
      <span
        className={cn(
          "mr-auto flex items-center gap-1",
          !item.selected &&
            "dark:text-foreground lg:dark:text-muted-foreground",
          item.selected && "dark:text-primaryaccent",
        )}
      >
        {item.title === "Settings" && variant !== "vertical" ? (
          <HybridTooltip>
            <HybridTooltipTrigger asChild>
              <Settings size={20} className="mr-2" />
            </HybridTooltipTrigger>
            <HybridTooltipContent>
              <p className="text-xs">Settings</p>
            </HybridTooltipContent>
          </HybridTooltip>
        ) : item.isUpgrade ? (
          <div className={"flex items-center gap-1"}>
            <Lightning width={13} height={16} /> Upgrade
          </div>
        ) : (
          <>
            {item.title}
            {item.external && (
              <ArrowExternal className="h-4 w-4" fill="currentColor" />
            )}
          </>
        )}
      </span>
      {item.label && <div>{item.label}</div>}
    </Link>
  );
};
export function Nav({
  items,
  compact = false,
  onNav,
  variant = "vertical",
  className,
}: NavProps) {
  const { currentOrgFull, isVercelManagedOrg } = useUser();
  const activeTier = getActiveTier(currentOrgFull);
  const shouldShowUpgrade = activeTier === "free" && !isVercelManagedOrg;
  const flag = useFeatureFlagEnabled("projects");

  const [isModifierKeyPressed, setIsModifierKeyPressed] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        setIsModifierKeyPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Meta" || e.key === "Control") {
        setIsModifierKeyPressed(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  return (
    <nav
      className={cn(
        "flex gap-1 font-montserrat m-0 px-0",
        variant === "horizontal" || variant === "tab"
          ? "flex-row m-0"
          : "flex-col",
        className,
      )}
    >
      {items.map((item, i) => (
        <div
          className={cn(
            "flex",
            item.group && i !== 0 && "mt-4",
            variant === "horizontal" || variant === "tab"
              ? "flex-row"
              : "flex-col",
          )}
          key={item.href || item.title || item.label}
        >
          {item.group ? (
            <Label
              key={item.title}
              className={cn(
                ":not:first:mt-6 mb-2 pr-2 text-xs uppercase text-foreground first:mt-2 first:pt-[5px] font-medium pl-0",
                compact ? "mb-1" : "mb-2",
              )}
            >
              {item.title === "Upgrade" ? (
                <span>
                  <Lightning /> Upgrade
                </span>
              ) : (
                item.title
              )}
            </Label>
          ) : (
            <>
              {item.href && item.href === "/settings/projects" ? (
                flag && (
                  <NavLink
                    item={item}
                    compact={compact}
                    onNav={onNav}
                    isModifierKeyPressed={isModifierKeyPressed}
                    variant={variant}
                    shouldShowUpgrade={shouldShowUpgrade}
                  />
                )
              ) : (
                <NavLink
                  item={item}
                  compact={compact}
                  onNav={onNav}
                  isModifierKeyPressed={isModifierKeyPressed}
                  variant={variant}
                  shouldShowUpgrade={shouldShowUpgrade}
                />
              )}
              {item.submenu && item.selected && (
                <div className="flex flex-col gap-1 mt-1">
                  {item.submenu.map((subitem) => {
                    const key = `subitem_${subitem.href}_${subitem.title}`;
                    return (
                      <NavLink
                        key={key}
                        item={subitem}
                        compact={compact}
                        sub
                        onNav={onNav}
                        isModifierKeyPressed={isModifierKeyPressed}
                        variant={variant}
                        shouldShowUpgrade={shouldShowUpgrade}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </nav>
  );
}
